import { coverageKindTakesSeveral, creditCoverageSourceForLevel, findCredit, findCreditScope } from "../../interfaces/framework/creditScopes";
import { allowsNotApplicable, findDeclarableScope, isCreditDeclarable, isMinimumExpectationDeclarable } from "../../interfaces/framework/declaredCredits";
/**
 * Keeps the links the catalogue asks for, warning about the rest: a level with
 * no source, a kind the level does not count, a repeat, or a second register
 * where a level counts one (`coverageKindTakesSeveral`). Levels above the one
 * pursued are kept, so changing level back finds them.
 */
export const resolveCoverageLinks = (entry, links, where) => {
    const kept = [];
    for (const link of links) {
        const wanted = entry && creditCoverageSourceForLevel(entry, link.level_id);
        const refusal = wanted?.kind !== link.source_kind
            ? "the credit counts no such source at that level"
            : kept.some((other) => other.level_id === link.level_id &&
                other.source_id === link.source_id)
                ? "it is already linked"
                : !coverageKindTakesSeveral[wanted.kind] &&
                    kept.some((other) => other.level_id === link.level_id)
                    ? "that level counts one"
                    : undefined;
        if (refusal) {
            console.warn(`Dropping coverage source "${link.source_kind}" "${link.source_id}" at level "${link.level_id}" on ${where}: ${refusal}`);
            continue;
        }
        kept.push(link);
    }
    return kept;
};
export const decodeProjectFramework = (projectFramework) => {
    return {
        country: projectFramework.country,
        created: new Date(projectFramework.created),
        credits: projectFramework.credits?.map((credit) => ({
            coverageSources: credit.coverage_sources?.length
                ? credit.coverage_sources.map((link) => ({
                    kind: link.source_kind,
                    levelId: link.level_id,
                    sourceId: link.source_id
                }))
                : undefined,
            creditCode: credit.credit_code,
            levelId: credit.level_id,
            pursued: credit.pursued
        })),
        declaredCredits: projectFramework.declared_credits?.map((credit) => ({
            creditCode: credit.credit_code,
            declaredBy: credit.declared_by ?? undefined,
            declaredPoints: credit.declared_points,
            minimumExpectationMet: credit.minimum_expectation_met ?? undefined,
            notApplicablePoints: credit.not_applicable_points,
            updated: credit.updated ? new Date(credit.updated) : undefined
        })),
        // Normalised for the same reason as `frameworkVersion` below: this reaches
        // the decoder off a raw DB row, and a nullable `text` column comes back as
        // `null`, not the `string | undefined` the type promises.
        frameworkAbbreviation: projectFramework.framework_abbreviation ?? undefined,
        frameworkId: projectFramework.framework_id,
        frameworkName: projectFramework.framework_name,
        // Normalised to `undefined` here, not just guarded at render: the field
        // reaches this decoder off a raw DB row rather than a `.parse()`, so a
        // never-versioned row arrives as `null`, not the `number | undefined` the
        // type promises. See `frameworkVersionedAbbreviationFor`.
        frameworkVersion: projectFramework.framework_version ?? undefined,
        id: projectFramework.project_framework_id,
        projectId: projectFramework.project_id,
        state: projectFramework.state,
        targetedRating: projectFramework.targeted_rating ?? undefined
    };
};
export const encodeProjectFramework = (projectFramework) => {
    return {
        country: projectFramework.country,
        created: projectFramework.created,
        credits: projectFramework.credits?.map((credit) => ({
            // Absent keeps the stored links; an empty list clears them.
            ...(credit.coverageSources && {
                coverage_sources: credit.coverageSources.map((link) => ({
                    level_id: link.levelId,
                    source_id: link.sourceId,
                    source_kind: link.kind
                }))
            }),
            credit_code: credit.creditCode,
            level_id: credit.levelId,
            pursued: credit.pursued
        })),
        // No `declared_credits`: `updateFrameworkDeclaredCredits` is their only
        // writer, and a set-up save carrying them re-stamped every answer.
        framework_abbreviation: projectFramework.frameworkAbbreviation,
        framework_id: projectFramework.frameworkId,
        framework_name: projectFramework.frameworkName,
        framework_version: projectFramework.frameworkVersion,
        project_framework_id: projectFramework.id,
        project_id: projectFramework.projectId,
        state: projectFramework.state,
        targeted_rating: projectFramework.targetedRating
    };
};
/**
 * Drops what the catalogue no longer knows from a stored framework's credit
 * selection, warning instead of throwing: a catalogue edit must never make a
 * project unreadable (#27 §8). A declared credit that has since become
 * evidenced or nil-scored is dropped too, which keeps the double-count guard
 * true without anyone deleting the stored answer (#105). A nil-scored credit
 * with a Minimum Expectation Catchment does not track is kept, at 0 points,
 * for its ME answer; an ME answer on a credit without one is dropped.
 */
export const resolveProjectFrameworkCredits = (projectFramework) => {
    const scope = findCreditScope(projectFramework.framework_id);
    const where = `project framework ${projectFramework.project_framework_id} (${projectFramework.framework_id})`;
    const credits = projectFramework.credits?.flatMap((credit) => {
        const entry = scope && findCredit(scope, credit.credit_code);
        if (!scope || !entry) {
            console.warn(`Skipping unknown credit "${credit.credit_code}" on ${where}`);
            return [];
        }
        const levelId = scope.levels.some((level) => level.id === credit.level_id)
            ? credit.level_id
            : scope.defaultLevelId;
        if (levelId !== credit.level_id) {
            console.warn(`Unknown level "${credit.level_id}" for credit "${credit.credit_code}" on ${where}; using "${scope.defaultLevelId}"`);
        }
        // A stale link would count against a threshold that has moved or gone;
        // dropped, it reads as "nothing linked yet" (#32). Low level to high.
        const rank = (link) => scope.levels.findIndex((level) => level.id === link.level_id);
        const coverageSources = credit.coverage_sources &&
            resolveCoverageLinks(entry, credit.coverage_sources, `credit "${credit.credit_code}" on ${where}`).sort((a, b) => rank(a) - rank(b));
        return [
            {
                ...credit,
                coverage_sources: coverageSources?.length
                    ? coverageSources
                    : undefined,
                level_id: levelId
            }
        ];
    });
    const declarable = findDeclarableScope(projectFramework.framework_id);
    const declaredCredits = projectFramework.declared_credits?.flatMap((credit) => {
        const entry = declarable?.credits.find((known) => known.creditCode === credit.credit_code);
        if (!entry) {
            console.warn(`Skipping unknown declared credit "${credit.credit_code}" on ${where}`);
            return [];
        }
        const meDeclarable = isMinimumExpectationDeclarable(projectFramework.framework_id, entry);
        const pointsDeclarable = isCreditDeclarable(projectFramework.framework_id, entry);
        if (!pointsDeclarable && !meDeclarable) {
            console.warn(`Dropping declared credit "${credit.credit_code}" on ${where}: it is no longer declarable`);
            return [];
        }
        // Capped, not dropped, if the scheme's points fell since it was answered.
        // A row kept only for its Minimum Expectation answer counts no points,
        // as the form saves it: they would count twice beside a tracked partner.
        const points = pointsDeclarable
            ? Math.min(Math.max(credit.declared_points, 0), entry.points)
            : 0;
        if (points !== credit.declared_points) {
            console.warn(`Clamping declared credit "${credit.credit_code}" on ${where} from ${credit.declared_points} to ${points}`);
        }
        // What is Not Applicable cannot also be expected, and a tool that
        // allows none keeps its whole total whatever was stored.
        const notApplicable = pointsDeclarable && allowsNotApplicable(projectFramework.framework_id)
            ? Math.min(Math.max(credit.not_applicable_points ?? 0, 0), entry.points - points)
            : 0;
        if (notApplicable !== (credit.not_applicable_points ?? 0)) {
            console.warn(`Clamping declared credit "${credit.credit_code}" Not Applicable on ${where} from ${credit.not_applicable_points} to ${notApplicable}`);
        }
        const meStale = credit.minimum_expectation_met != null && !meDeclarable;
        if (meStale) {
            console.warn(`Dropping Minimum Expectation answer for credit "${credit.credit_code}" on ${where}: it has none to answer`);
        }
        const { minimum_expectation_met: _dropped, ...rest } = credit;
        return [
            {
                ...(meStale ? rest : credit),
                declared_points: points,
                // Absent means "not sent": a save keeps the stored figure.
                ...(credit.not_applicable_points === undefined
                    ? {}
                    : { not_applicable_points: notApplicable })
            }
        ];
    });
    const rating = projectFramework.targeted_rating;
    const ratingKnown = rating == null || !!scope?.ratingScale.some((step) => step.id === rating);
    if (!ratingKnown) {
        console.warn(`Skipping unknown targeted rating "${rating}" on ${where}`);
    }
    return {
        ...projectFramework,
        credits,
        declared_credits: declaredCredits,
        targeted_rating: ratingKnown ? rating : undefined
    };
};
export const decodeOrganisationFrameworkSummary = (summary) => ({
    country: summary.country ?? undefined,
    frameworkAbbreviation: summary.framework_abbreviation ?? undefined,
    frameworkId: summary.framework_id,
    frameworkName: summary.framework_name,
    projectCount: summary.project_count,
    state: summary.state ?? undefined
});
